import type { AccordionBlockContent } from "../types"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface AccordionBlockEditorProps {
  content: AccordionBlockContent | null
  onChange: (content: AccordionBlockContent) => void
}

export function AccordionBlockEditor({ content, onChange }: AccordionBlockEditorProps) {
  const c = content ?? {}
  const items = c.items ?? [{ question: "", answer: "" }]
  const update = (partial: Partial<AccordionBlockContent>) =>
    onChange({ ...c, ...partial })
  const setItem = (i: number, field: "question" | "answer", val: string) => {
    const next = [...items]
    if (!next[i]) next[i] = { question: "", answer: "" }
    next[i] = { ...next[i], [field]: val }
    update({ items: next })
  }
  const addItem = () => update({ items: [...items, { question: "", answer: "" }] })
  const removeItem = (i: number) =>
    update({ items: items.filter((_, j) => j !== i) })

  return (
    <Card>
      <CardHeader>
        <h3 className="text-base font-medium">Accordion</h3>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Heading</Label>
          <Input
            value={c.heading ?? ""}
            onChange={(e) => update({ heading: e.target.value })}
            placeholder="Frequently asked questions"
          />
        </div>
        <div>
          <Label>Columns</Label>
          <Select
            value={String(c.columns ?? 1)}
            onValueChange={(v) => update({ columns: Number(v) as 1 | 2 })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">1</SelectItem>
              <SelectItem value="2">2</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-3">
          <Label>Items</Label>
          {items.map((item, i) => (
            <div key={i} className="rounded-md border p-3 space-y-2">
              <Input
                placeholder="Question"
                value={item.question ?? ""}
                onChange={(e) => setItem(i, "question", e.target.value)}
              />
              <Textarea
                placeholder="Answer"
                value={item.answer ?? ""}
                onChange={(e) => setItem(i, "answer", e.target.value)}
                rows={2}
                className="resize-none"
              />
              <Button type="button" variant="ghost" size="sm" onClick={() => removeItem(i)}>
                Remove
              </Button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={addItem}>
            Add item
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
